import { Skeleton } from "@credit-acceptance/uikit";
import { FileText, MessageSquare } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { getErrorMessage } from "../../lib/query/errors";
import { cn } from "../../lib/utils";
import { useReadOnly } from "../lock/ReadOnlyContext";
import type { DocumentOrMessage } from "./api/types";
import { useDocumentsAndMessages } from "./api/useDocumentsAndMessages";
import { mapDocumentsAndMessages } from "./documentsAndMessages.mapping";
import { useDocumentViewerStore } from "./documentViewerStore";
import { openDocumentViewerWindow } from "./openViewerWindow";
import SendProofsModal from "./SendProofsModal";

interface DocumentsAndMessagesPanelProperties {
  approvalId: string;
  /** Dealer id from the deal summary, forwarded to proof uploads. */
  dealerId?: string;
}

/**
 * Formats a fixed-width `"YYYY-MM-DD HH:mm:ss"` (or ISO) timestamp for display as
 * `MM/DD/YY h:mm AM`. The space-separated form is normalized to ISO-ish
 * (`T` separator) so parsing is deterministic across engines. Returns an empty
 * string for a missing timestamp so message rows without one render cleanly.
 * Note: the BFF sends no timezone, so the value is rendered in the viewer's
 * local time (intended).
 * @param {string} value - The raw timestamp string.
 * @returns {string} The formatted timestamp, or "" when absent/invalid.
 */
const formatReceived = (value: string) => {
  if (!value) {
    return "";
  }
  const date = new Date(value.replace(" ", "T"));
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  const datePart = date.toLocaleDateString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "2-digit",
  });
  const timePart = date.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
  return `${datePart} ${timePart}`;
};

function DocumentsAndMessagesPanel({ approvalId, dealerId }: DocumentsAndMessagesPanelProperties) {
  const { t } = useTranslation();
  const readOnly = useReadOnly();
  const { data, isLoading, isError, error } = useDocumentsAndMessages(approvalId);
  const view = data ? mapDocumentsAndMessages(data) : undefined;
  const [sendProofsOpen, setSendProofsOpen] = useState(false);
  const setFocusedDocument = useDocumentViewerStore((state) => state.setFocusedDocument);

  // Open a viewable document in the dual-window viewer (OR-22558): record the
  // selection for the broadcaster (mounted in ReviewPage), then open/refocus the
  // viewer window. The window's mount-time handshake pulls this current payload,
  // so the viewer loads even though it opens after this call.
  const openDocument = (item: DocumentOrMessage) => {
    if (item.kind !== "doc" || !item.viewable || !item.docId) {
      return;
    }
    setFocusedDocument({
      appId: approvalId,
      docId: item.docId,
      fileName: item.fileName ?? item.name,
      contentType: item.contentType ?? "application/octet-stream",
      reviewedBy: item.reviewedBy,
    });
    openDocumentViewerWindow();
  };

  return (
    <section className="border-border bg-card flex flex-col overflow-hidden rounded-lg border">
      <header className="border-border bg-section-header sticky top-0 z-10 flex items-center justify-between gap-2 border-b px-4 py-1.5">
        <div className="flex min-w-0 items-center gap-2">
          <h2 className="text-foreground text-sm font-semibold">{t("documents.heading")}</h2>
          {view && view.unreadMessageCount > 0 && (
            <Badge variant="destructive" aria-label={t("documents.unreadBadge", { count: view.unreadMessageCount })}>
              {view.unreadMessageCount}
            </Badge>
          )}
        </div>
        <Button size="xs" onClick={() => setSendProofsOpen(true)} disabled={readOnly}>
          {t("documents.sendProofs")}
        </Button>
      </header>

      <SendProofsModal open={sendProofsOpen} onOpenChange={setSendProofsOpen} appId={approvalId} dealerId={dealerId} />

      {isLoading && (
        <div role="status" aria-label={t("documents.loading")} className="p-4">
          <Skeleton loading active paragraph={{ rows: 3 }} />
        </div>
      )}

      {isError && (
        <p role="alert" className="text-destructive px-4 py-3 text-xs">
          {t("documents.loadError", { message: getErrorMessage(error) })}
        </p>
      )}

      {view && view.items.length === 0 && (
        <p className="text-muted-foreground px-4 py-6 text-center text-xs">{t("documents.empty")}</p>
      )}

      {view && view.items.length > 0 && (
        <ul
          // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- intentional: makes this scrollable region keyboard-scrollable (WCAG 2.1.1); the list itself stays non-interactive.
          tabIndex={0}
          aria-label={t("documents.listLabel")}
          className="divide-border h-[225px] divide-y overflow-y-auto"
        >
          {view.items.map((item) => {
            const isUnreviewedDocument = item.kind === "doc" && !item.reviewedBy;
            const isUnread = item.unread === true || isUnreviewedDocument;
            // Assumes all "Processing" status documents are FAX proofs (currently the only optimistic upload type).
            const isProcessing = item.kind === "doc" && item.name === "FAX" && item.receivedAt === "Processing";
            const isOpenable = !isProcessing && item.kind === "doc" && item.viewable === true && Boolean(item.docId);
            const icon =
              item.kind === "doc" ? <FileText className="size-3.5" /> : <MessageSquare className="size-3.5" />;
            const nameClasses = cn(
              "min-w-0 flex-1 truncate text-[0.625rem]",
              isUnread ? "font-semibold" : "font-normal"
            );
            return (
              <li
                key={item.id}
                className="hover:bg-muted grid grid-cols-12 items-center gap-2 py-1 pl-2 pr-4 text-xs transition-colors"
              >
                {isOpenable && !readOnly ? (
                  <button
                    type="button"
                    onClick={() => openDocument(item)}
                    aria-label={t("documents.openDocument", { name: item.name })}
                    className="text-foreground col-span-6 flex min-w-0 items-center gap-1.5 text-left underline-offset-2 hover:underline focus-visible:underline"
                  >
                    <span aria-hidden="true" className="text-muted-foreground shrink-0">
                      {icon}
                    </span>
                    <span className={nameClasses}>{item.name}</span>
                  </button>
                ) : (
                  <span className="text-foreground col-span-6 flex min-w-0 items-center gap-1.5">
                    <span aria-hidden="true" className="text-muted-foreground shrink-0">
                      {icon}
                    </span>
                    <span className={nameClasses}>{item.name}</span>
                  </span>
                )}
                <span
                  className={cn(
                    "text-muted-foreground col-span-4 truncate text-[0.625rem]",
                    isUnread ? "font-semibold" : "font-normal"
                  )}
                >
                  {formatReceived(item.receivedAt)}
                </span>
                <span
                  className={cn(
                    "text-muted-foreground col-span-2 truncate text-[0.625rem] uppercase tracking-wide",
                    isUnread ? "font-semibold" : "font-normal"
                  )}
                >
                  {item.kind === "doc" ? item.reviewedBy ?? "" : ""}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

export default DocumentsAndMessagesPanel;
